import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from './../../context/auth-context.jsx';
import { adminApi } from './../../lib/admin-api/index.js';
import { FIELD_TYPE_OPTIONS, OPTION_FIELD_TYPES, normalizeFields, normalizeSteps, toRow } from './kyc-shared.js';
import { normalizeImpactsForSourceType, toApiField, toEditableField } from './kyc-step-utils.js';
import {
  addOptionToField,
  removeFieldOption,
  supportsOptions,
  updateFieldOption,
} from './kyc-step-options-logic.js';
import KycStepFieldsEditor from './kyc-step-fields-editor.jsx';
import KycStepOptionsModal from './kyc-step-options-modal.jsx';
import KycStepPreview from './kyc-step-preview.jsx';

function normalizeCountryOptions(payload) {
  const unwrap = (value) => {
    if (Array.isArray(value)) {
      return value;
    }
    if (value && typeof value === 'object') {
      return Object.values(value);
    }
    return [];
  };

  const sourceCandidates = [
    payload,
    payload?.countries,
    payload?.country,
    payload?.items,
    payload?.data,
    payload?.dictionary,
    payload?.dictionaries,
  ];

  const allItems = [];
  sourceCandidates.forEach((candidate) => {
    unwrap(candidate).forEach((item) => allItems.push(item));
  });

  const map = new Map();
  allItems.forEach((item) => {
    if (typeof item === 'string') {
      const value = item.trim();
      if (value) {
        map.set(value, { value, label: value });
      }
      return;
    }

    if (!item || typeof item !== 'object') {
      return;
    }

    const rawValue = item.code || item.alpha2 || item.alpha3 || item.country || item.value || item.uid || item.id || '';
    const value = String(rawValue || '').trim();
    if (!value) {
      return;
    }

    const label = String(item.name || item.title || value).trim() || value;
    map.set(value, { value, label: label === value ? value : `${value} - ${label}` });
  });

  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
}

function normalizeDependencies(value) {
  return normalizeFields(value)
    .map((item) => (item && typeof item === 'object' ? item : {}));
}

function getEntityId(value) {
  return String(value?.id || value?.uid || '').trim();
}

function getFieldEntityId(value) {
  return String(value?.id || value?.fieldId || value?.uid || '').trim();
}

function getDependencyEntityId(value) {
  return String(value?.id || value?.uid || value?.dependencyId || '').trim();
}

function pickFirstObject(candidates) {
  for (const candidate of candidates) {
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
      return candidate;
    }
  }
  return null;
}

function KycStepPage({ mode, basePath = '/system/user-settings/kyc' }) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const jurisdictionId = String(searchParams.get('jurisdictionId') || '').trim();
  const resolvedBasePath = String(basePath || '/system/user-settings/kyc').replace(/\/+$/, '') || '/system/user-settings/kyc';
  const jurisdictionQuery = jurisdictionId
    ? `?jurisdictionId=${encodeURIComponent(jurisdictionId)}`
    : '';
  const kycListPath = jurisdictionQuery
    ? `${resolvedBasePath}${jurisdictionQuery}`
    : resolvedBasePath;

  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const canEdit = isCreate || isEdit;
  const localeHint = typeof navigator !== 'undefined' && navigator.language
    ? navigator.language
    : 'en-US';

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [currentStepId, setCurrentStepId] = useState(id || '');
  const [saveTrigger, setSaveTrigger] = useState(0);
  const [dragSourceLocalId, setDragSourceLocalId] = useState('');

  const [order, setOrder] = useState(0);
  const [form, setForm] = useState({
    name: '',
    description: '',
    immutable: false,
    fields: [],
  });
  const [newField, setNewField] = useState({ name: '', type: 'text' });
  const [countryOptions, setCountryOptions] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [optionsModal, setOptionsModal] = useState({
    isOpen: false,
    targetLocalId: '',
    targetFieldId: '',
    newOption: '',
  });
  const [deleteFieldTarget, setDeleteFieldTarget] = useState(null);

  const knownStepIdsRef = useRef(new Set());
  const lastSavedSignatureRef = useRef('');
  const lastAttemptedSignatureRef = useRef('');
  const formFieldsRef = useRef([]);
  const lastProcessedSaveTriggerRef = useRef(0);
  const pendingSaveActionRef = useRef(null);

  const preserveFieldLocalIds = useCallback((currentFields, nextFields) => {
    const byKey = new Map();
    const currentList = normalizeFields(currentFields);
    const nextList = normalizeFields(nextFields);
    const usedLocalIds = new Set();

    currentList.forEach((field) => {
      const keys = [
        String(field?.fieldId || '').trim(),
        String(field?.source?.id || '').trim(),
        String(field?.source?.uid || '').trim(),
        String(field?.source?.fieldId || '').trim(),
      ].filter(Boolean);

      keys.forEach((key) => {
        if (!byKey.has(key)) {
          byKey.set(key, field.localId);
        }
      });
    });

    const nextWithMatchMeta = nextList.map((field) => {
      const keys = [
        String(field?.fieldId || '').trim(),
        String(field?.source?.id || '').trim(),
        String(field?.source?.uid || '').trim(),
        String(field?.source?.fieldId || '').trim(),
      ].filter(Boolean);

      const preservedLocalId = keys.map((key) => byKey.get(key)).find(Boolean);
      if (preservedLocalId) {
        usedLocalIds.add(preservedLocalId);
        return { field: { ...field, localId: preservedLocalId }, matched: true };
      }

      return { field, matched: false };
    });

    // Fallback for newly created fields: when server assigns IDs after first save,
    // keep local row identity by matching still-unmatched fields on stable shape.
    return nextWithMatchMeta.map((item) => {
      if (item.matched) {
        return item.field;
      }

      const field = item.field;

      const matchIndex = currentList.findIndex((currentField) => {
        if (!currentField?.localId || usedLocalIds.has(currentField.localId)) {
          return false;
        }

        const sameName = String(currentField?.name || '').trim() === String(field?.name || '').trim();
        const sameType = String(currentField?.type || '').trim() === String(field?.type || '').trim();
        const sameRequired = Boolean(currentField?.required) === Boolean(field?.required);
        const sameOptionsCount = normalizeFields(currentField?.options).length === normalizeFields(field?.options).length;

        return sameName && sameType && sameRequired && sameOptionsCount;
      });

      if (matchIndex < 0) {
        return field;
      }

      const matchedLocalId = currentList[matchIndex].localId;
      usedLocalIds.add(matchedLocalId);
      return { ...field, localId: matchedLocalId };
    });
  }, []);

  useEffect(() => {
    setCurrentStepId(id || '');
  }, [id]);

  useEffect(() => {
    formFieldsRef.current = form.fields;
  }, [form.fields]);

  const loadCountryOptions = useCallback(async () => {
    try {
      const response = await adminApi.getCountryDictionary(token);

      setCountryOptions(normalizeCountryOptions(response));
    } catch (requestError) {
      if (requestError?.status === 404) {
        setCountryOptions([]);
        return;
      }

      setCountryOptions([]);
    }
  }, [token]);

  const buildPayload = useCallback((formValue, orderValue) => ({
    name: String(formValue?.name || '').trim(),
    description: String(formValue?.description || '').trim(),
    immutable: Boolean(formValue?.immutable),
    fields: normalizeFields(formValue?.fields).map(toApiField),
    order: orderValue,
  }), []);

  const buildValidationErrors = useCallback((formValue) => {
    const nextErrors = {};

    if (!String(formValue?.name || '').trim()) {
      nextErrors.name = t('kyc.fieldRequired');
    }

    if (!String(formValue?.description || '').trim()) {
      nextErrors.description = t('kyc.fieldRequired');
    }

    const fields = normalizeFields(formValue?.fields);
    const hasInvalidField = fields.some((field) => !String(field.name || '').trim() || !FIELD_TYPE_OPTIONS.includes(field.type));
    if (hasInvalidField) {
      nextErrors.fields = t('kyc.fieldsInvalid');
    }

    const hasInvalidOptions = fields.some((field) => {
      if (!OPTION_FIELD_TYPES.has(field.type)) {
        return false;
      }

      const options = Array.isArray(field.options) ? field.options : [];
      return options.length === 0 || options.some((option) => !String(option || '').trim());
    });

    if (hasInvalidOptions) {
      nextErrors.fields = t('kyc.fieldsOptionsInvalid');
    }

    return nextErrors;
  }, [t]);

  const previewFields = useMemo(
    () => form.fields.map((field) => {
      const apiField = toApiField(field);
      return {
        ...apiField,
        id: String(field.fieldId || '').trim(),
        uid: String(field.source?.uid || '').trim(),
        sourceId: String(field.source?.id || '').trim(),
      };
    }),
    [form.fields],
  );

  const pageTitle = useMemo(() => {
    if (isCreate) {
      return t('kyc.addStepTitle');
    }
    if (isEdit) {
      return t('kyc.editStepTitle');
    }
    return t('kyc.viewStepTitle');
  }, [isCreate, isEdit, t]);

  const optionsTargetField = useMemo(
    () => form.fields.find((field) => {
      if (field.localId === optionsModal.targetLocalId) {
        return true;
      }

      if (!optionsModal.targetFieldId) {
        return false;
      }

      const fieldId = String(field.fieldId || '').trim();
      const sourceId = String(field.source?.id || '').trim();
      const sourceFieldId = String(field.source?.fieldId || '').trim();
      return [fieldId, sourceId, sourceFieldId].includes(optionsModal.targetFieldId);
    }) || null,
    [form.fields, optionsModal.targetLocalId, optionsModal.targetFieldId],
  );

  useEffect(() => {
    if (!optionsModal.isOpen) {
      return;
    }

    if (!optionsTargetField || !supportsOptions(optionsTargetField.type)) {
      setOptionsModal({
        isOpen: false,
        targetLocalId: '',
        targetFieldId: '',
        newOption: '',
      });
    }
  }, [optionsModal.isOpen, optionsTargetField]);

  const findStepInPayload = useCallback((payload, targetStepId, fallbackPayload = null) => {
    const steps = normalizeSteps(payload).map(toRow);
    if (targetStepId) {
      const byId = steps.find((step) => String(step.id) === String(targetStepId));
      if (byId) {
        return byId;
      }
    }

    if (fallbackPayload) {
      const byBody = steps.find((step) => (
        String(step.name || '').trim() === String(fallbackPayload.name || '').trim()
        && String(step.description || '').trim() === String(fallbackPayload.description || '').trim()
        && Number(step.order) === Number(fallbackPayload.order)
      ));
      if (byBody) {
        return byBody;
      }
    }

    return steps.length > 0 ? steps[steps.length - 1] : null;
  }, []);

  const buildFieldAliasMap = useCallback((fields) => {
    const map = new Map();

    normalizeFields(fields).forEach((field) => {
      const localId = String(field?.localId || '').trim();
      if (!localId) {
        return;
      }

      const keys = [
        String(field?.fieldId || '').trim(),
        String(field?.source?.id || '').trim(),
        String(field?.source?.uid || '').trim(),
        String(field?.source?.fieldId || '').trim(),
      ].filter(Boolean);

      keys.forEach((key) => map.set(key, localId));
    });

    return map;
  }, []);

  const applyApiFieldErrors = useCallback((requestError) => {
    const nextErrors = {};
    const errors = Array.isArray(requestError?.errors) ? requestError.errors : [];

    errors.forEach((item) => {
      const parameter = String(item?.parameter || '').trim();
      const message = String(item?.message || 'Invalid value').trim();

      if (['name', 'description'].includes(parameter)) {
        nextErrors[parameter] = message;
        return;
      }

      if (parameter === 'fields') {
        nextErrors.fields = message;
      }
    });

    if (Object.keys(nextErrors).length) {
      setFormErrors(nextErrors);
    }
  }, []);

  const hydrateFieldFromServer = useCallback(async (stepId, localId, preferredFieldId = '') => {
    const fieldId = String(preferredFieldId || '').trim();
    if (!stepId || !localId || !fieldId || fieldId.startsWith('field-id-')) {
      return;
    }

    const response = await adminApi.getKycField(token, jurisdictionId, stepId, fieldId);
    const serverField = pickFirstObject([
      response?.field,
      response?.data?.field,
      normalizeFields(response?.fields)[0],
      normalizeFields(response?.data?.fields)[0],
    ]);

    if (!serverField) {
      return;
    }

    setForm((prev) => ({
      ...prev,
      fields: prev.fields.map((field, index) => (
        field.localId === localId
          ? { ...toEditableField(serverField, index), localId: field.localId }
          : field
      )),
    }));
  }, [token, jurisdictionId]);

  const hydrateFieldDependenciesFromServer = useCallback(async (stepId, localId, fieldId) => {
    const resolvedFieldId = String(fieldId || '').trim();
    if (!stepId || !localId || !resolvedFieldId || resolvedFieldId.startsWith('field-id-')) {
      return;
    }

    const response = await adminApi.getKycFieldDependencies(token, jurisdictionId, stepId, resolvedFieldId);
    const dependenciesSource = normalizeFields(
      response?.dependencies
      ?? response?.fieldDependencies
      ?? response?.items
      ?? response,
    );
    const normalized = dependenciesSource
      .map((item) => (item && typeof item === 'object' ? item : null))
      .filter(Boolean)
      .map((dependency) => ({
        id: String(dependency.id || dependency.uid || '').trim(),
        fieldId: String(dependency.fieldIdRelated || dependency.fieldId || '').trim(),
        impacts: normalizeFields(dependency.impacts ?? dependency.values).map((value) => String(value ?? '').trim()).filter(Boolean),
        checked: typeof dependency.checked === 'boolean' ? dependency.checked : null,
        source: { ...dependency },
      }))
      .filter((dependency) => dependency.fieldId);

    setForm((prev) => ({
      ...prev,
      fields: prev.fields.map((field) => (
        field.localId === localId
          ? { ...field, dependencies: normalized }
          : field
      )),
    }));
  }, [token, jurisdictionId]);

  const loadStep = useCallback(async () => {
    if (!jurisdictionId) {
      setNotFound(true);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');
    setNotFound(false);

    try {
      const response = await adminApi.getKycSettings(token, jurisdictionId);

      const steps = normalizeSteps(response).map(toRow);
      knownStepIdsRef.current = new Set(steps.map((step) => String(step.id || '')));

      if (isCreate) {
        setOrder(steps.length);
        lastSavedSignatureRef.current = JSON.stringify(buildPayload({ name: '', description: '', immutable: false, fields: [] }, steps.length));
        lastAttemptedSignatureRef.current = '';
        return;
      }

      const target = steps.find((step) => String(step.id) === String(id));
      if (!target) {
        setNotFound(true);
        return;
      }

      const editableFields = preserveFieldLocalIds(formFieldsRef.current, normalizeFields(target.fields).map(toEditableField));
      const nextOrder = Number.isFinite(target.order) ? target.order : 0;

      setOrder(nextOrder);
      setForm({
        name: target.name,
        description: target.description,
        immutable: Boolean(target.immutable),
        fields: editableFields,
      });

      lastSavedSignatureRef.current = JSON.stringify(buildPayload({
        name: target.name,
        description: target.description,
        immutable: Boolean(target.immutable),
        fields: editableFields,
      }, nextOrder));
      lastAttemptedSignatureRef.current = '';
    } catch (requestError) {
      if (requestError?.status === 404) {
        if (isCreate) {
          knownStepIdsRef.current = new Set();
          setOrder(0);
          lastSavedSignatureRef.current = JSON.stringify(buildPayload({ name: '', description: '', immutable: false, fields: [] }, 0));
          lastAttemptedSignatureRef.current = '';
          return;
        }

        setNotFound(true);
        return;
      }

      setError(requestError.message || 'Failed to load KYC step');
    } finally {
      setIsLoading(false);
    }
  }, [token, isCreate, id, buildPayload, preserveFieldLocalIds, jurisdictionId]);

  useEffect(() => {
    loadStep();
  }, [loadStep]);

  useEffect(() => {
    loadCountryOptions();
  }, [loadCountryOptions]);

  const savePayload = useCallback(async (payload, signature) => {
    if (!canEdit) {
      return;
    }

    setIsSaving(true);
    setError('');

    const previousKnownIds = new Set(knownStepIdsRef.current);

    try {
      const stepPayload = {
        name: String(payload?.name || '').trim(),
        description: String(payload?.description || '').trim(),
        immutable: Boolean(payload?.immutable),
        position: Number.isFinite(payload?.order) ? payload.order : 0,
      };

      let nextStepId = String(currentStepId || '').trim();
      if (nextStepId) {
        await adminApi.updateKycStep(token, jurisdictionId, nextStepId, stepPayload);
      } else {
        const createResponse = await adminApi.createKycStep(token, jurisdictionId, stepPayload);
        nextStepId = getEntityId(createResponse) || getEntityId(createResponse?.step);
      }

      const afterStepResponse = await adminApi.getKycSettings(token, jurisdictionId);
      const afterStepRows = normalizeSteps(afterStepResponse).map(toRow);
      knownStepIdsRef.current = new Set(afterStepRows.map((step) => String(step.id || '')));

      let savedStep = findStepInPayload(afterStepResponse, nextStepId, stepPayload);
      if (!nextStepId && savedStep) {
        const candidate = afterStepRows.find((step) => !previousKnownIds.has(String(step.id || '')));
        if (candidate) {
          savedStep = candidate;
        }
      }

      if (!savedStep) {
        throw new Error('Saved step not found in jurisdiction response');
      }

      nextStepId = String(savedStep.id || '').trim();

      const localFields = normalizeFields(formFieldsRef.current);
      const aliasMap = buildFieldAliasMap(localFields);
      const localToServerFieldId = new Map();
      const serverFieldsBeforeSync = normalizeFields(savedStep.fields);
      const serverFieldIdsBeforeSync = new Set(
        serverFieldsBeforeSync
          .map((field) => getFieldEntityId(field))
          .filter(Boolean),
      );
      const retainedServerFieldIds = new Set();

      for (let index = 0; index < localFields.length; index += 1) {
        const field = localFields[index];
        const fieldPayload = { ...toApiField(field), position: index };
        delete fieldPayload.dependencies;

        const fieldSourceCandidates = [
          String(field?.source?.id || '').trim(),
          String(field?.source?.uid || '').trim(),
          String(field?.source?.fieldId || '').trim(),
          String(field?.fieldId || '').trim(),
        ].filter(Boolean);

        const existingServerId = fieldSourceCandidates
          .find((candidate) => serverFieldIdsBeforeSync.has(candidate)) || '';

        if (existingServerId) {
          await adminApi.updateKycField(token, jurisdictionId, nextStepId, existingServerId, fieldPayload);
          retainedServerFieldIds.add(existingServerId);
          localToServerFieldId.set(field.localId, existingServerId);
          continue;
        }

        const createdField = await adminApi.createKycField(token, jurisdictionId, nextStepId, fieldPayload);
        const createdFieldId = getFieldEntityId(createdField) || getFieldEntityId(createdField?.field);
        if (createdFieldId) {
          retainedServerFieldIds.add(createdFieldId);
          localToServerFieldId.set(field.localId, createdFieldId);
        }
      }

      for (const serverFieldId of serverFieldIdsBeforeSync) {
        if (!retainedServerFieldIds.has(serverFieldId)) {
          await adminApi.deleteKycField(token, jurisdictionId, nextStepId, serverFieldId);
        }
      }

      const afterFieldsResponse = await adminApi.getKycSettings(token, jurisdictionId);
      const stepAfterFields = findStepInPayload(afterFieldsResponse, nextStepId, stepPayload);
      const serverFieldsAfterSync = normalizeFields(stepAfterFields?.fields);

      localFields.forEach((field, index) => {
        if (localToServerFieldId.has(field.localId)) {
          return;
        }

        const bySource = serverFieldsAfterSync.find((serverField) => (
          getFieldEntityId(serverField) && (
            getFieldEntityId(serverField) === String(field?.source?.id || '').trim()
            || getFieldEntityId(serverField) === String(field?.source?.uid || '').trim()
            || getFieldEntityId(serverField) === String(field?.fieldId || '').trim()
          )
        ));
        if (bySource) {
          localToServerFieldId.set(field.localId, getFieldEntityId(bySource));
          return;
        }

        const byPosition = serverFieldsAfterSync[index];
        if (byPosition) {
          const byPositionId = getFieldEntityId(byPosition);
          if (byPositionId) {
            localToServerFieldId.set(field.localId, byPositionId);
          }
        }
      });

      for (const field of localFields) {
        const targetFieldId = localToServerFieldId.get(field.localId);
        if (!targetFieldId) {
          continue;
        }

        const serverField = serverFieldsAfterSync.find((item) => getFieldEntityId(item) === targetFieldId) || null;
        const serverDependencies = normalizeDependencies(serverField?.dependencies);
        const currentDependency = serverDependencies[0] || null;
        const desiredDependency = normalizeDependencies(field.dependencies)[0] || null;

        if (!desiredDependency) {
          for (const dependency of serverDependencies) {
            const dependencyId = getDependencyEntityId(dependency);
            if (dependencyId) {
              await adminApi.deleteKycDependency(token, jurisdictionId, nextStepId, targetFieldId, dependencyId);
            }
          }
          continue;
        }

        const dependencySourceLocalId = aliasMap.get(String(desiredDependency.fieldId || '').trim())
          || localFields.find((item) => String(item.fieldId || '').trim() === String(desiredDependency.fieldId || '').trim())?.localId;
        const dependencySourceFieldId = dependencySourceLocalId
          ? localToServerFieldId.get(dependencySourceLocalId)
          : '';
        const dependencySourceField = dependencySourceLocalId
          ? localFields.find((item) => item.localId === dependencySourceLocalId)
          : null;

        if (!dependencySourceFieldId) {
          continue;
        }

        const dependencyPayload = {
          fieldIdRelated: dependencySourceFieldId,
          impacts: normalizeImpactsForSourceType(
            desiredDependency.impacts,
            dependencySourceField?.type,
            localeHint,
          ),
          checked: typeof desiredDependency.checked === 'boolean' ? desiredDependency.checked : null,
        };

        const dependencyId = String(
          desiredDependency.id
          || desiredDependency.source?.id
          || desiredDependency.source?.uid
          || getDependencyEntityId(currentDependency)
          || '',
        ).trim();

        if (dependencyId) {
          await adminApi.updateKycDependency(token, jurisdictionId, nextStepId, targetFieldId, dependencyId, dependencyPayload);
        } else {
          await adminApi.createKycDependency(token, jurisdictionId, nextStepId, targetFieldId, dependencyPayload);
        }
      }

      const finalResponse = await adminApi.getKycSettings(token, jurisdictionId);
      const finalRows = normalizeSteps(finalResponse).map(toRow);
      knownStepIdsRef.current = new Set(finalRows.map((step) => String(step.id || '')));
      const finalStep = findStepInPayload(finalResponse, nextStepId, stepPayload);

      if (finalStep) {
        const normalizedFields = preserveFieldLocalIds(formFieldsRef.current, normalizeFields(finalStep.fields).map(toEditableField));
        const nextOrder = Number.isFinite(finalStep.order) ? finalStep.order : payload.order;
        const nextForm = {
          name: finalStep.name || payload.name,
          description: finalStep.description || payload.description,
          immutable: typeof finalStep.immutable === 'boolean' ? finalStep.immutable : Boolean(payload.immutable),
          fields: normalizedFields,
        };

        setOrder(nextOrder);
        setForm(nextForm);
        setFormErrors({});

        const canonicalSignature = JSON.stringify(buildPayload(nextForm, nextOrder));
        lastSavedSignatureRef.current = canonicalSignature;
        lastAttemptedSignatureRef.current = '';

        if (!currentStepId && finalStep.id) {
          const createdStepId = String(finalStep.id);
          setCurrentStepId(createdStepId);
          navigate(`${resolvedBasePath}/${encodeURIComponent(createdStepId)}/edit${jurisdictionQuery}`, { replace: true });
        }
      } else {
        lastSavedSignatureRef.current = signature;
        lastAttemptedSignatureRef.current = '';
      }
    } catch (requestError) {
      applyApiFieldErrors(requestError);
      setError(requestError.message || 'Failed to save step');
    } finally {
      setIsSaving(false);
    }
  }, [
    token,
    currentStepId,
    canEdit,
    applyApiFieldErrors,
    buildPayload,
    navigate,
    preserveFieldLocalIds,
    jurisdictionId,
    findStepInPayload,
    buildFieldAliasMap,
    jurisdictionQuery,
    localeHint,
    resolvedBasePath,
  ]);

  const triggerSave = useCallback((action = null) => {
    pendingSaveActionRef.current = action;
    setSaveTrigger((prev) => prev + 1);
  }, []);

  const processSaveAction = useCallback(async (action) => {
    if (!action || !canEdit) {
      return;
    }

    if (action.type === 'legacy-full-sync') {
      const payload = buildPayload(form, order);
      const signature = JSON.stringify(payload);
      await savePayload(payload, signature);
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      let stepId = String(currentStepId || '').trim();
      const stepPayload = {
        name: String(form.name || '').trim(),
        description: String(form.description || '').trim(),
        immutable: Boolean(form.immutable),
        position: Number.isFinite(order) ? order : 0,
      };

      const ensureStepId = async () => {
        if (stepId) {
          return stepId;
        }

        const nextErrors = buildValidationErrors({ ...form, fields: [] });
        if (nextErrors.name || nextErrors.description) {
          setFormErrors((prev) => ({ ...prev, ...nextErrors }));
          return '';
        }

        const created = await adminApi.createKycStep(token, jurisdictionId, stepPayload);
        stepId = getEntityId(created) || getEntityId(created?.step);
        if (stepId) {
          setCurrentStepId(stepId);
          navigate(`${resolvedBasePath}/${encodeURIComponent(stepId)}/edit${jurisdictionQuery}`, { replace: true });
        }
        return stepId;
      };

      if (action.type === 'step') {
        const nextErrors = buildValidationErrors({ ...form, fields: [] });
        if (nextErrors.name || nextErrors.description) {
          setFormErrors((prev) => ({ ...prev, ...nextErrors }));
          return;
        }

        if (stepId) {
          await adminApi.updateKycStep(token, jurisdictionId, stepId, stepPayload);
        } else {
          await ensureStepId();
        }
      }

      if (action.type === 'field-upsert') {
        const ensuredStepId = await ensureStepId();
        if (!ensuredStepId) {
          return;
        }

        const fields = normalizeFields(formFieldsRef.current);
        const field = fields.find((item) => item.localId === action.localId);
        if (!field) {
          return;
        }

        const fieldIndex = fields.findIndex((item) => item.localId === action.localId);
        const fieldPayload = { ...toApiField(field), position: fieldIndex };
        delete fieldPayload.dependencies;

        const persistedId = String(field?.fieldId || '').trim();
        const isNewField = !persistedId || persistedId.startsWith('field-id-');

        if (isNewField) {
          const createdField = await adminApi.createKycField(token, jurisdictionId, ensuredStepId, fieldPayload);
          const createdFieldId = getFieldEntityId(createdField) || getFieldEntityId(createdField?.field);
          if (createdFieldId) {
            await hydrateFieldFromServer(ensuredStepId, action.localId, createdFieldId);
          }
        } else {
          await adminApi.updateKycField(token, jurisdictionId, ensuredStepId, persistedId, fieldPayload);
          await hydrateFieldFromServer(ensuredStepId, action.localId, persistedId);
        }
      }

      if (action.type === 'field-delete' && action.fieldId && !String(action.fieldId).startsWith('field-id-')) {
        const ensuredStepId = await ensureStepId();
        if (!ensuredStepId) {
          return;
        }
        await adminApi.deleteKycField(token, jurisdictionId, ensuredStepId, action.fieldId);
      }

      if (action.type === 'dependency-upsert') {
        const ensuredStepId = await ensureStepId();
        if (!ensuredStepId) {
          return;
        }

        const fields = normalizeFields(formFieldsRef.current);
        const targetField = fields.find((item) => item.localId === action.localId);
        if (!targetField) {
          return;
        }

        const targetFieldId = String(targetField?.fieldId || '').trim();
        if (!targetFieldId || targetFieldId.startsWith('field-id-')) {
          return;
        }

        const desiredDependency = normalizeDependencies(targetField.dependencies)[0] || null;
        if (!desiredDependency) {
          return;
        }

        const sourceField = fields.find((item) => String(item.fieldId || '').trim() === String(desiredDependency.fieldId || '').trim());
        const sourceFieldId = String(sourceField?.fieldId || '').trim();
        if (!sourceFieldId || sourceFieldId.startsWith('field-id-')) {
          return;
        }

        const dependencyPayload = {
          fieldIdRelated: sourceFieldId,
          impacts: normalizeImpactsForSourceType(
            desiredDependency.impacts,
            sourceField?.type,
            localeHint,
          ),
          checked: typeof desiredDependency.checked === 'boolean' ? desiredDependency.checked : null,
        };

        const dependencyId = String(
          desiredDependency.id
          || desiredDependency.source?.id
          || desiredDependency.source?.uid
          || '',
        ).trim();

        if (dependencyId) {
          await adminApi.updateKycDependency(token, jurisdictionId, ensuredStepId, targetFieldId, dependencyId, dependencyPayload);
        } else {
          await adminApi.createKycDependency(token, jurisdictionId, ensuredStepId, targetFieldId, dependencyPayload);
        }

        await hydrateFieldDependenciesFromServer(ensuredStepId, action.localId, targetFieldId);
      }

      if (
        action.type === 'dependency-delete'
        && action.fieldId
        && !String(action.fieldId).startsWith('field-id-')
      ) {
        const ensuredStepId = await ensureStepId();
        if (!ensuredStepId) {
          return;
        }
        const dependencyIds = normalizeFields(action.dependencyIds)
          .map((item) => String(item || '').trim())
          .filter(Boolean);
        for (const dependencyId of dependencyIds) {
          await adminApi.deleteKycDependency(token, jurisdictionId, ensuredStepId, action.fieldId, dependencyId);
        }

        const targetField = normalizeFields(formFieldsRef.current)
          .find((field) => String(field.fieldId || '').trim() === String(action.fieldId || '').trim());
        if (targetField) {
          await hydrateFieldDependenciesFromServer(ensuredStepId, targetField.localId, action.fieldId);
        }
      }

      if (action.type === 'fields-reorder') {
        const ensuredStepId = await ensureStepId();
        if (!ensuredStepId) {
          return;
        }

        const fields = normalizeFields(formFieldsRef.current);
        for (let index = 0; index < fields.length; index += 1) {
          const field = fields[index];
          const persistedId = String(field?.fieldId || '').trim();
          if (!persistedId || persistedId.startsWith('field-id-')) {
            continue;
          }
          const fieldPayload = { ...toApiField(field), position: index };
          delete fieldPayload.dependencies;
          await adminApi.updateKycField(token, jurisdictionId, ensuredStepId, persistedId, fieldPayload);
        }
      }

      setFormErrors({});
    } catch (requestError) {
      applyApiFieldErrors(requestError);
      setError(requestError.message || 'Failed to save step');
    } finally {
      setIsSaving(false);
    }
  }, [
    token,
    canEdit,
    currentStepId,
    form,
    order,
    jurisdictionId,
    buildValidationErrors,
    buildPayload,
    navigate,
    applyApiFieldErrors,
    savePayload,
    hydrateFieldFromServer,
    hydrateFieldDependenciesFromServer,
    jurisdictionQuery,
    localeHint,
    resolvedBasePath,
  ]);

  useEffect(() => {
    if (!canEdit || isLoading || notFound || isSaving) {
      return;
    }

    if (saveTrigger === 0 || saveTrigger === lastProcessedSaveTriggerRef.current) {
      return;
    }

    lastProcessedSaveTriggerRef.current = saveTrigger;
    const action = pendingSaveActionRef.current;
    pendingSaveActionRef.current = null;
    processSaveAction(action);
  }, [canEdit, isLoading, notFound, isSaving, saveTrigger, processSaveAction]);

  const addFieldRow = () => {
    const name = String(newField.name || '').trim();
    const type = FIELD_TYPE_OPTIONS.includes(newField.type) ? newField.type : 'text';

    if (!name) {
      return;
    }

    const newLocalId = `field-new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const newFieldId = `field-id-new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

    setForm((prev) => ({
      ...prev,
      fields: [
        ...prev.fields,
        {
          localId: newLocalId,
          fieldId: newFieldId,
          name,
          type,
          required: false,
          immutable: false,
          source: {},
          options: [],
          dependencies: [],
        },
      ],
    }));

    setNewField({ name: '', type: 'text' });

    if (OPTION_FIELD_TYPES.has(type)) {
      setOptionsModal({
        isOpen: true,
        targetLocalId: newLocalId,
        targetFieldId: newFieldId,
        newOption: '',
      });
      return;
    }

    triggerSave({ type: 'field-upsert', localId: newLocalId });
  };

  const duplicateFieldRow = (localId) => {
    const now = Date.now();
    const unique = Math.random().toString(36).slice(2, 6);
    const duplicatedLocalId = `field-new-${now}-${unique}`;
    const duplicatedFieldId = `field-id-new-${now}-${unique}`;

    setForm((prev) => {
      const sourceField = prev.fields.find((field) => field.localId === localId);
      if (!sourceField) {
        return prev;
      }

      const clonedSource = sourceField.source && typeof sourceField.source === 'object' ? { ...sourceField.source } : {};
      delete clonedSource.id;
      delete clonedSource.fieldId;

      const duplicatedField = {
        ...sourceField,
        localId: duplicatedLocalId,
        fieldId: duplicatedFieldId,
        source: clonedSource,
        options: Array.isArray(sourceField.options) ? [...sourceField.options] : [],
        dependencies: normalizeFields(sourceField.dependencies).map((dependency) => {
          const sourceDependency = dependency && typeof dependency === 'object' ? { ...dependency } : {};
          delete sourceDependency.id;
          delete sourceDependency.source;
          return {
            ...sourceDependency,
            impacts: normalizeFields(dependency?.impacts).map((value) => String(value ?? '')),
          };
        }),
      };

      const sourceIndex = prev.fields.findIndex((field) => field.localId === localId);
      const nextFields = [...prev.fields];
      nextFields.splice(sourceIndex + 1, 0, duplicatedField);

      return {
        ...prev,
        fields: nextFields,
      };
    });
    triggerSave({ type: 'field-upsert', localId: duplicatedLocalId });
  };

  const reorderFields = (fields, sourceLocalId, targetLocalId) => {
    const sourceIndex = fields.findIndex((field) => field.localId === sourceLocalId);
    const targetIndex = fields.findIndex((field) => field.localId === targetLocalId);

    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
      return fields;
    }

    const nextFields = [...fields];
    const [movedField] = nextFields.splice(sourceIndex, 1);
    nextFields.splice(targetIndex, 0, movedField);
    return nextFields;
  };

  const handleFieldDragStart = (event, localId, rowElement) => {
    if (isSaving) {
      return;
    }

    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', localId);
    if (rowElement && event.dataTransfer?.setDragImage) {
      event.dataTransfer.setDragImage(rowElement, 16, 16);
    }
    setDragSourceLocalId(localId);
  };

  const handleFieldDragEnd = () => {
    setDragSourceLocalId('');
  };

  const handleFieldDrop = (targetLocalId) => {
    if (!dragSourceLocalId || dragSourceLocalId === targetLocalId || isSaving) {
      setDragSourceLocalId('');
      return;
    }

    setForm((prev) => ({
      ...prev,
      fields: reorderFields(prev.fields, dragSourceLocalId, targetLocalId),
    }));
    setDragSourceLocalId('');
    triggerSave({ type: 'fields-reorder' });
  };

  const removeFieldRow = (localId) => {
    let deletedPersistedFieldId = '';
    setForm((prev) => {
      const deletedField = prev.fields.find((field) => field.localId === localId);
      const deletedFieldId = deletedField?.fieldId;
      deletedPersistedFieldId = String(deletedFieldId || '').trim();

      return {
        ...prev,
        fields: prev.fields
          .filter((field) => field.localId !== localId)
          .map((field) => ({
            ...field,
            dependencies: normalizeFields(field.dependencies).filter((dependency) => dependency?.fieldId !== deletedFieldId),
          })),
      };
    });
    triggerSave({ type: 'field-delete', fieldId: deletedPersistedFieldId });
  };

  const requestRemoveField = (localId) => {
    const targetField = form.fields.find((field) => field.localId === localId);
    if (!targetField) {
      return;
    }

    setDeleteFieldTarget({
      localId,
      name: String(targetField.name || '').trim() || 'this field',
    });
  };

  const cancelRemoveField = () => {
    setDeleteFieldTarget(null);
  };

  const confirmRemoveField = () => {
    if (!deleteFieldTarget?.localId) {
      return;
    }

    removeFieldRow(deleteFieldTarget.localId);
    setDeleteFieldTarget(null);
  };

  const updateFieldRow = (localId, key, value) => {
    setForm((prev) => ({
      ...prev,
      fields: prev.fields.map((field) => (field.localId === localId ? { ...field, [key]: value } : field)),
    }));
  };

  const toggleMandatory = (localId, checked) => {
    updateFieldRow(localId, 'required', checked);
    triggerSave({ type: 'field-upsert', localId });
  };

  const toggleFieldImmutable = (localId, currentImmutable) => {
    updateFieldRow(localId, 'immutable', !Boolean(currentImmutable));
    triggerSave({ type: 'field-upsert', localId });
  };

  const toggleStepImmutable = () => {
    setForm((prev) => ({ ...prev, immutable: !Boolean(prev.immutable) }));
    triggerSave({ type: 'step' });
  };

  const toggleVisible = (localId, currentVisible) => {
    updateFieldRow(localId, 'visible', !Boolean(currentVisible));
    triggerSave({ type: 'field-upsert', localId });
  };

  const openDependencyModal = (localId) => {
    const targetField = form.fields.find((field) => field.localId === localId);
    if (!targetField) {
      return;
    }

    const fieldId = String(targetField.fieldId || '').trim();
    if (!fieldId || fieldId.startsWith('field-id-')) {
      setError(t('kyc.pleaseSelectField'));
      return;
    }

    navigate(`${resolvedBasePath}/${encodeURIComponent(currentStepId)}/edit/field/${encodeURIComponent(fieldId)}/dependencies${jurisdictionQuery}`);
  };

  const openOptionsModal = (localId) => {
    const targetField = form.fields.find((field) => field.localId === localId);
    if (!targetField || !supportsOptions(targetField.type)) {
      return;
    }

    setOptionsModal({
      isOpen: true,
      targetLocalId: localId,
      targetFieldId: String(targetField.fieldId || '').trim(),
      newOption: '',
    });
  };

  const closeOptionsModal = () => {
    setOptionsModal({
      isOpen: false,
      targetLocalId: '',
      targetFieldId: '',
      newOption: '',
    });
  };

  const updateOptionValue = (optionIndex, value) => {
    if (!optionsTargetField) {
      return;
    }

    setForm((prev) => ({
      ...prev,
      fields: updateFieldOption(prev.fields, optionsTargetField.localId, optionIndex, value),
    }));
  };

  const removeOptionValue = (optionIndex) => {
    if (!optionsTargetField) {
      return;
    }

    setForm((prev) => ({
      ...prev,
      fields: removeFieldOption(prev.fields, optionsTargetField.localId, optionIndex),
    }));
    triggerSave({ type: 'field-upsert', localId: optionsTargetField.localId });
  };

  const addOptionValue = () => {
    if (!optionsTargetField) {
      return;
    }

    const rawValue = String(optionsModal.newOption || '');
    if (!rawValue.trim()) {
      return;
    }

    setForm((prev) => ({
      ...prev,
      fields: addOptionToField(prev.fields, optionsTargetField.localId, rawValue),
    }));
    setOptionsModal((prev) => ({ ...prev, newOption: '' }));
    triggerSave({ type: 'field-upsert', localId: optionsTargetField.localId });
  };

  if (!jurisdictionId) {
    return <Navigate to={resolvedBasePath} replace />;
  }

  if (!isCreate && !id) {
    return <Navigate to={kycListPath} replace />;
  }

  if (notFound) {
    return <Navigate to={kycListPath} replace />;
  }

  return (
    <div>
      <div className="app-page-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2>{pageTitle}</h2>
          <p>{t('kyc.stepConfigSubtitle')}</p>
        </div>
        <button type="button" className="btn btn-default" onClick={() => navigate(kycListPath)}>{t('common.back')}</button>
      </div>

      <div className="widget-content searchable-container list">
        {error ? <div className="alert alert-danger py-2">{error}</div> : null}

        {isLoading ? (
          <div className="card card-body">
            <div className="text-muted">{t('kyc.loading')}</div>
          </div>
        ) : (
          <form onSubmit={(event) => event.preventDefault()}>
            <div className="card card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="fw-semibold">{t('kyc.stepDetails')}</div>
                <button
                  type="button"
                  className={`btn btn-sm app-icon-toggle ${form.immutable ? 'is-active' : ''}`}
                  onClick={toggleStepImmutable}
                  disabled={isSaving || !canEdit}
                  title={canEdit ? t('kyc.immutableStep') : t('kyc.immutableStepReadOnly')}
                >
                  <i className="fa fa-lock"></i>
                </button>
              </div>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">{t('kyc.labelName')}</label>
                  <input
                    className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                    value={form.name}
                    onChange={(event) => {
                      setForm((prev) => ({ ...prev, name: event.target.value }));
                    }}
                    onBlur={() => triggerSave({ type: 'step' })}
                    disabled={!canEdit}
                    required
                  />
                  {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">{t('kyc.labelDescription')}</label>
                  <input
                    className={`form-control ${formErrors.description ? 'is-invalid' : ''}`}
                    value={form.description}
                    onChange={(event) => {
                      setForm((prev) => ({ ...prev, description: event.target.value }));
                    }}
                    onBlur={() => triggerSave({ type: 'step' })}
                    disabled={!canEdit}
                    required
                  />
                  {formErrors.description && <div className="invalid-feedback">{formErrors.description}</div>}
                </div>
              </div>
            </div>

            <div className="card card-body mt-3">
              <div className="fw-semibold mb-3">{t('kyc.fieldsSection')}</div>
              {canEdit ? (
                <KycStepFieldsEditor
                  fields={form.fields}
                  newField={newField}
                  isSaving={isSaving}
                  dragSourceLocalId={dragSourceLocalId}
                  onUpdateField={updateFieldRow}
                  onToggleMandatory={toggleMandatory}
                  onToggleImmutable={toggleFieldImmutable}
                  onToggleVisible={toggleVisible}
                  onFieldBlur={(localId) => triggerSave({ type: 'field-upsert', localId })}
                  onDuplicateField={duplicateFieldRow}
                  onRemoveField={requestRemoveField}
                  onFieldDragStart={handleFieldDragStart}
                  onFieldDragEnd={handleFieldDragEnd}
                  onFieldDrop={handleFieldDrop}
                  onOpenOptions={openOptionsModal}
                  onOpenDependency={openDependencyModal}
                  onChangeNewField={(key, value) => setNewField((prev) => ({ ...prev, [key]: value }))}
                  onAddField={addFieldRow}
                />
              ) : (
                <KycStepPreview fields={previewFields} countries={countryOptions} />
              )}

              {formErrors.fields && <div className="text-danger small mt-2">{formErrors.fields}</div>}
            </div>
          </form>
        )}
      </div>

      <KycStepOptionsModal
        isOpen={optionsModal.isOpen}
        fieldName={optionsTargetField?.name || ''}
        options={Array.isArray(optionsTargetField?.options) ? optionsTargetField.options : []}
        newOption={optionsModal.newOption}
        onNewOptionChange={(newOption) => setOptionsModal((prev) => ({ ...prev, newOption }))}
        onAddOption={addOptionValue}
        onUpdateOption={updateOptionValue}
        onRemoveOption={removeOptionValue}
        onOptionBlur={() => triggerSave({ type: 'field-upsert', localId: optionsTargetField?.localId })}
        onClose={closeOptionsModal}
      />

      {deleteFieldTarget && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{t('kyc.deleteFieldTitle')}</h5>
                  <button type="button" className="btn-close" onClick={cancelRemoveField}></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    {t('kyc.deleteFieldConfirm')} <strong>{deleteFieldTarget.name}</strong>?
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-default" onClick={cancelRemoveField} disabled={isSaving}>{t('common.cancel')}</button>
                  <button type="button" className="btn btn-danger" onClick={confirmRemoveField} disabled={isSaving}>{t('common.delete')}</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

    </div>
  );
}

export default KycStepPage;
